import React, { useRef, useEffect } from 'react';
import './QBackground.css';

const FONTS = ['monospace', 'serif', 'sans-serif', 'Arial', 'Verdana'];

const QBackground = ({ onNodesConnected, onNodeMissed }) => {
  const canvasRef = useRef(null);
  const particles = useRef([]);
  const animationFrameId = useRef(null);

  const PARTICLE_COUNT = 40;
  const NEURON_DISTANCE = 150;
  const CLICK_RADIUS = 80; // Hitbox ligeramente ampliado para mejor UX
  const FADE_SPEED = 0.015;

  useEffect(() => {
    const createParticle = (width, height, reseed = false) => {
      return {
        x: Math.random() * width,
        y: reseed ? height + 50 : Math.random() * height,
        speedY: 0.5 + Math.random() * 1.5,
        size: 12 + Math.random() * 28,
        font: FONTS[Math.floor(Math.random() * FONTS.length)],
        opacity: 0.2 + Math.random() * 0.4,
        isNeuron: false,
        fadeTimer: 1.0
      };
    };

    const initParticles = (width, height) => {
      const newParticles = [];
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        newParticles.push(createParticle(width, height));
      }
      particles.current = newParticles;
    };

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      if (particles.current.length === 0) {
        initParticles(canvas.width, canvas.height);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    const handleClick = (e) => {
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      let connectedCount = 0;

      particles.current.forEach(p => {
        const dx = p.x - mouseX;
        const dy = p.y - mouseY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < CLICK_RADIUS) {
          if (!p.isNeuron) {
            p.isNeuron = true;
            connectedCount++;
          }
          // Mark neighbors as part of the neuron cluster
          particles.current.forEach(neighbor => {
            const ndx = neighbor.x - p.x;
            const ndy = neighbor.y - p.y;
            const dist = Math.sqrt(ndx * ndx + ndy * ndy);
            if (dist < NEURON_DISTANCE) {
              if (!neighbor.isNeuron) {
                neighbor.isNeuron = true;
                connectedCount++;
              }
            }
          });
        }
      });

      if (connectedCount > 0 && typeof onNodesConnected === 'function') {
        onNodesConnected(connectedCount);
      }
    };

    canvas.addEventListener('click', handleClick);

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.current.forEach((p, index) => {
        // Update position
        p.y -= p.speedY;

        // Reset if out of bounds
        if (p.y < -50) {
          if (!p.isNeuron && onNodeMissed) {
            onNodeMissed();
          }
          particles.current[index] = createParticle(canvas.width, canvas.height, true);
          return;
        }

        // Handle Fading
        if (p.isNeuron) {
          p.fadeTimer -= FADE_SPEED;
          if (p.fadeTimer <= 0) {
            particles.current[index] = createParticle(canvas.width, canvas.height, true);
            return;
          }
        }

        const currentOpacity = p.isNeuron ? p.opacity * p.fadeTimer : p.opacity;

        // Synapse Logic: If this is a neuron, connect to nearby letters
        if (p.isNeuron) {
          particles.current.forEach(other => {
            if (p === other) return;
            const dx = other.x - p.x;
            const dy = other.y - p.y;
            const dist = Math.sqrt(dx * dx + dy * dy);

            if (dist < NEURON_DISTANCE) {
              const otherOpacity = other.isNeuron ? other.opacity * other.fadeTimer : other.opacity;
              const lineOpacity = Math.min(currentOpacity, otherOpacity) * 0.5;
              ctx.beginPath();
              ctx.strokeStyle = `rgba(0, 150, 136, ${lineOpacity})`; // Teal color
              ctx.lineWidth = 1;
              ctx.moveTo(p.x, p.y);
              ctx.lineTo(other.x, other.y);
              ctx.stroke();
            }
          });
        }

        // Render 'Q'
        ctx.save();
        ctx.fillStyle = `rgba(128, 128, 128, ${currentOpacity})`;
        ctx.font = `${p.size}px ${p.font}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('Q', p.x, p.y);
        ctx.restore();
      });

      animationFrameId.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      canvas.removeEventListener('click', handleClick);
      cancelAnimationFrame(animationFrameId.current);
    };
  }, [onNodesConnected, onNodeMissed]);

  return (
    <div className="q-background-container">
      <canvas ref={canvasRef} style={{ display: 'block' }} />
    </div>
  );
};

export default QBackground;
